import { useEffect, useMemo, useState } from "react";

import shapesYaml from "../../../../packages/material-db/data/shapes.yaml?raw";

// Large stamps are shipped as gzipped YAML files under
// `apps/web/public/shapes_large/`, served as static assets at the
// root URL. Keeping them out of the JS bundle is essential — at
// the byte scale Snake's RAM needs (17 MB gz / 320 MB raw for the
// 8x1024 variants, 0.5–2 MB for the 8x64 and 1x1024 ones), inlining
// them via `?raw` or `?url` would balloon Vite/Rollup's build
// memory past Node's default heap (CI OOM at ~2 GB).
const LARGE_SHAPE_URLS = [
  "/shapes_large/demo_computer_2bit_pc_8x8.yaml.gz",
  "/shapes_large/demo_cpu_2bit_pc_seq_decoder.yaml.gz",
  "/shapes_large/ram_8x64_vertical.yaml.gz",
  "/shapes_large/ram_8x64_vertical_snake.yaml.gz",
  "/shapes_large/ram_1x1024.yaml.gz",
  "/shapes_large/ram_8x1024_blank.yaml.gz",
  "/shapes_large/ram_8x1024_snake.yaml.gz",
];
import type { SavedExperiment, SavedFormula } from "../auth/client";
import { buildResolver } from "../formula/latex";
import type { MaterialResolver } from "../formula/types";
import {
  groupShapes,
  loadGzippedShapeDoc,
  parseShapeDoc,
  type ShapeDef,
} from "../sim/shapes";
import type { MaterialDef, SandboxBundle, SandboxLike } from "../sim/types";

import { CoursesModal } from "../courses/CoursesModal";
import { useCustomCourses } from "../courses/useCustomCourses";
import { CoursePanel } from "./CoursePanel";
import { useCourseRunner } from "../courses/useCourseRunner";
import { PuzzlesModal } from "../puzzles/PuzzlesModal";
import { usePuzzleRunner } from "../puzzles/usePuzzleRunner";

import { AuthModal, type AuthMode } from "./AuthModal";
import { CodePanel } from "./CodePanel";
import { ExperimentPanel } from "./ExperimentPanel";
import { Footer } from "./Footer";
import { FormulaPanel } from "./FormulaPanel";
import { LibraryPanel } from "./LibraryPanel";
import { Mascot } from "./Mascot";
import { ShapesPanel } from "./ShapesPanel";
import { SimCanvas } from "./SimCanvas";
import { Sidebar } from "./Sidebar";
import { ThreeDView } from "./ThreeDView";
import { Full3DView } from "./Full3DView";
import { AtomicView } from "./AtomicView";
import material3dYaml from "../../../../packages/material-db/data/material-3d.yaml?raw";
import { parseShapes3dDoc, shape3dFor } from "../sim/shapes3d";
import { Toolbar } from "./Toolbar";

// Selectable world resolutions. Bigger = more, smaller cells (the
// canvas stays the same on-screen size, so pixels shrink — use the
// magnifier to inspect and a radius-0 brush to paint single cells).
// 256 stays the default so existing scenes / courses are unchanged;
// the larger sizes give room for big gate circuits (a gate CPU).
// The uniform-field diffusion skip and the unchanged-tick signal-pass
// skip keep them interactive: an idle/while-building world tick is
// ~2 ms at 512, ~10 ms at 1024, ~40 ms at 2048 (so 2048 is best for
// building — pause physics, or run smaller circuits there).
const WORLD_SIZES = [256, 512, 1024, 2048] as const;
const DEFAULT_WORLD_SIZE = 256;
const DEFAULT_PIXEL_SCALE = 2;
const MOBILE_PIXEL_SCALE = 1.5;
const MOBILE_BREAKPOINT_PX = 768;

// Discrete canvas-zoom rungs. Sub-1 entries let a 2048 world fit a
// laptop viewport (0.25x = 512 px wide, 0.5x = 1024 px); 1x..6x is
// the historical range. zoomStepUp/Down walk this array so the
// fractional and integer levels share a single ladder.
const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4, 5, 6] as const;
const MIN_PIXEL_SCALE = ZOOM_STEPS[0];
const MAX_PIXEL_SCALE = ZOOM_STEPS[ZOOM_STEPS.length - 1];

function zoomStepUp(current: number): number {
  return ZOOM_STEPS.find((v) => v > current) ?? MAX_PIXEL_SCALE;
}
function zoomStepDown(current: number): number {
  return [...ZOOM_STEPS].reverse().find((v) => v < current) ?? MIN_PIXEL_SCALE;
}

function isMobileViewport(): boolean {
  return typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT_PX;
}

function initialPixelScale(): number {
  // 256x256 grid is 384 px on mobile at 1.5x — comfortably under
  // even a 360 px-wide phone, with room for the toolbar gutter.
  // Desktop / tablet keeps the historical 2x default.
  return isMobileViewport() ? MOBILE_PIXEL_SCALE : DEFAULT_PIXEL_SCALE;
}

const DEFAULT_FORMULA_SOURCE = "2 hydrogen + oxygen -> 2 water @ T=800K, ticks=80";
const DEFAULT_EXPERIMENT_JSON = JSON.stringify(
  {
    name: "tiny_boil",
    grid: { width: 256, height: 256 },
    seed: 1,
    actions: [
      { tick: 0, kind: "paint", params: { material: "water", x: 128, y: 128, r: 6 } },
      { tick: 0, kind: "heat", params: { x: 128, y: 128, r: 12, kelvin: 400 } },
    ],
    expectations: [
      { tick: 100, kind: "expect_count", params: { material: "steam", at_least: 1 } },
    ],
  },
  null,
  2,
);

export function App() {
  const [materials, setMaterials] = useState<MaterialDef[]>([]);
  const [sandbox, setSandbox] = useState<SandboxLike | null>(null);
  const [selected, setSelected] = useState(0);
  const [previousSelected, setPreviousSelected] = useState(0);
  const [eraser, setEraser] = useState(false);
  const [brushRadius, setBrushRadius] = useState(4);
  // 293 K ≈ 20 °C, the same value `WorldConfig::ambient_temperature_k`
  // initialises the temperature field to. The user can change this
  // via the "Room temperature" slider — that re-targets every empty
  // cell's temperature to the new value AND resets the brush
  // default so the next stroke starts from the same baseline.
  const [roomTemperatureK, setRoomTemperatureK] = useState(293);
  const [brushKelvin, setBrushKelvin] = useState(293);
  const [heatMode, setHeatMode] = useState(false);
  // Action tool: when on, single-click on the canvas toggles the
  // `tags: [trigger]` cell under the cursor (e.g. on/off
  // switches) instead of painting. Mutually exclusive with
  // heat / eraser / shape so a click can never do two things at
  // once. Double-click on the canvas keeps toggling regardless
  // of mode — that path was wired before the action tool
  // existed and is kept as a power-user shortcut.
  const [actionMode, setActionMode] = useState(false);
  // Code tool: when on, single-click on a `cpu` machine (the chip or
  // its RAM / screen) opens the Code Tool panel showing the program
  // inside it — disassembled ISA + an editable assembly buffer you
  // can load back. Mutually exclusive with the other tools.
  const [codeMode, setCodeMode] = useState(false);
  // The CPU cell the Code Tool panel is editing. Null = panel closed.
  const [codeTarget, setCodeTarget] = useState<{ x: number; y: number } | null>(null);
  // Dimension Switch tool: clicking a `dimension` portal cell enters
  // its child world. Mutually exclusive with the other tools.
  const [dimensionMode, setDimensionMode] = useState(false);
  // Atomic Mode: pause the world, drag-select a hotspot, convert those
  // materials to a LAMMPS system, and run a wrapped atomify (molecular
  // dynamics) in an iframe over the canvas. Sibling of the Dimension tool.
  const [atomicMode, setAtomicMode] = useState(false);
  // How many dimensions deep the viewed layer is (0 = main world).
  const [layerDepth, setLayerDepth] = useState(0);
  // 3D layer-stack view: a paused snapshot of every layer's materials
  // rendered as stacked planes you can orbit around.
  const [threeDMode, setThreeDMode] = useState(false);
  // Full 3D Mode: a standalone first-person 3D scene (dirt ground +
  // sun) that takes over the canvas. Independent of the 2D sim and
  // of the layer-stack `threeDMode` above.
  const [full3DMode, setFull3DMode] = useState(false);
  const [paused, setPaused] = useState(false);
  const [tickInfo, setTickInfo] = useState({ tick: 0, backend: "loading" });
  const [selectedShape, setSelectedShape] = useState<ShapeDef | null>(null);
  const [bgIndex, setBgIndex] = useState(0);
  // Auth modal state lives at the App level so any panel (Toolbar,
  // FormulaPanel, ExperimentPanel) can prompt the user to sign in
  // when they try to save without being authenticated.
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<AuthMode>("signin");
  // Courses modal + runner. The modal is the "open the book" picker;
  // the runner is the state machine that walks the chosen course
  // step-by-step against the live sandbox.
  const [coursesOpen, setCoursesOpen] = useState(false);
  const courseRunner = useCourseRunner(sandbox, setPaused);
  // User-generated (Text-to-Course) courses: localStorage cache +
  // backend DB persistence, merged into the Courses book.
  const { courses: customCourses, addCourse } = useCustomCourses();
  const addCustomCourse = (course: Parameters<typeof addCourse>[0]) => {
    addCourse(course);
    setCoursesOpen(true); // surface it immediately
  };
  const [puzzlesOpen, setPuzzlesOpen] = useState(false);
  const puzzleRunner = usePuzzleRunner(sandbox);
  const openAuthModal = (mode: AuthMode) => {
    setAuthModalMode(mode);
    setAuthModalOpen(true);
  };
  // Lifted from FormulaPanel / ExperimentPanel so the LibraryPanel
  // can write a saved row's content back into the right textarea.
  // Both panels are fully-controlled inputs now.
  const [formulaSource, setFormulaSource] = useState(DEFAULT_FORMULA_SOURCE);
  const [experimentJson, setExperimentJson] = useState(DEFAULT_EXPERIMENT_JSON);
  // Canvas zoom + magnifier overlay state. Both live here so the
  // Footer toolbar (under the canvas) and SimCanvas (which renders
  // them) can stay siblings rather than nesting one inside the
  // other for state-sharing reasons.
  // World grid resolution (square). Changing it recreates the
  // sandbox at the new size (SimCanvas re-inits on width/height).
  const [worldSize, setWorldSize] = useState<number>(DEFAULT_WORLD_SIZE);
  const [pixelScale, setPixelScale] = useState(initialPixelScale);
  const [magnifierActive, setMagnifierActive] = useState(false);
  const [magnifierZoom, setMagnifierZoom] = useState(3);
  // Zoom-tool: when active, clicking the canvas zooms one rung in
  // centred on the clicked grid cell (shift-click zooms one rung
  // out). The canvas-host scroll lands on the same screen point so
  // the cell under the cursor stays put. Mutually exclusive with
  // the other click-driven tools (action / code / dimension).
  const [zoomToolActive, setZoomToolActive] = useState(false);
  // Move tool: when active, click-and-drag pans the canvas-host
  // scroll position (like dragging a map); double-click lifts the
  // 4-connected same-material region under the cursor, and the next
  // single click drops it at the new location. Mutually exclusive
  // with the other click-driven tools.
  const [moveMode, setMoveMode] = useState(false);
  // Mobile-only: drawer state for the two sidebars. Desktop ignores
  // these (CSS keeps the sidebars docked); on narrow viewports the
  // sidebars become slide-in overlays toggled from hamburger buttons
  // in the toolbar. Defaults match the mobile UX request: materials
  // open on first paint (so the user can pick a brush immediately),
  // formulas/experiments closed (low-frequency tools).
  const [leftNavOpen, setLeftNavOpen] = useState(true);
  const [rightNavOpen, setRightNavOpen] = useState(false);
  // Walk the discrete zoom ladder (`ZOOM_STEPS`) — same ladder the
  // zoom-tool uses, so the +/- buttons and the click-tool stay in
  // step.
  const zoomIn = () => setPixelScale((s) => zoomStepUp(s));
  const zoomOut = () => setPixelScale((s) => zoomStepDown(s));

  const onLoadExperiment = (row: SavedExperiment) => {
    // Pretty-print the JSON before dropping it into the textarea so
    // the user sees a readable structure rather than a one-line blob.
    try {
      setExperimentJson(JSON.stringify(row.payload, null, 2));
    } catch {
      setExperimentJson(String(row.payload));
    }
  };
  const onLoadFormula = (row: SavedFormula) => {
    setFormulaSource(row.source);
  };

  const BACKGROUNDS = [
    { label: "Space", className: "bg-space" },
    { label: "Black", className: "bg-black" },
    { label: "Night", className: "bg-night" },
    { label: "Slate", className: "bg-slate" },
    { label: "Sunset", className: "bg-sunset" },
    { label: "Cream", className: "bg-cream" },
  ];
  const cycleBackground = () => setBgIndex((i) => (i + 1) % BACKGROUNDS.length);
  const bg = BACKGROUNDS[bgIndex];

  const resolver = useMemo<MaterialResolver>(() => buildResolver(materials), [materials]);
  // 3D shape table for Full 3D Mode (id → block/grains/liquid/…),
  // parsed once. `shapeFor` resolves a material to its keyword with a
  // per-phase fallback for anything the file doesn't name.
  const shapes3dTable = useMemo(() => parseShapes3dDoc(material3dYaml), []);
  const shapeFor = useMemo(
    () => (def: MaterialDef | null) => shape3dFor(def, shapes3dTable),
    [shapes3dTable],
  );
  const baseShapes = useMemo(() => parseShapeDoc(shapesYaml), []);
  // Large stamps live as gzipped YAML side-files so they don't bloat
  // the initial JS bundle. They lazy-load after mount and merge into
  // the shape catalogue; the UI shows the base shapes immediately and
  // each heavy stamp pops in once its `.yaml.gz` is fetched +
  // decompressed (smallest first, since they finish fastest).
  const [largeShapes, setLargeShapes] = useState<ShapeDef[]>([]);
  useEffect(() => {
    void Promise.all(LARGE_SHAPE_URLS.map(loadGzippedShapeDoc)).then((groups) =>
      setLargeShapes(groups.flat()),
    );
  }, []);
  const shapes = useMemo(() => [...baseShapes, ...largeShapes], [baseShapes, largeShapes]);
  // Single search query drives both the materials list inside the
  // Sidebar *and* the Shapes panel rendered below it. Lifting the
  // state up here lets the same string filter both lists from one
  // input. Sidebar consumes this via `filter` / `onFilterChange`
  // props; ShapesPanel sees the already-filtered groups below.
  const [paletteFilter, setPaletteFilter] = useState("");
  const shapeGroups = useMemo(() => {
    const all = groupShapes(shapes);
    const needle = paletteFilter.trim().toLowerCase();
    if (needle.length === 0) return all;
    // Match on id, display name, icon name, and category so a
    // user typing "torch", "flame", or "fire" all surface the
    // torch shape; equipment shapes carry icons that read like
    // search keywords.
    return all
      .map((g) => ({
        ...g,
        members: g.members.filter((s) => {
          const haystack = `${s.id} ${s.name} ${s.icon} ${g.category}`.toLowerCase();
          return haystack.includes(needle);
        }),
      }))
      .filter((g) => g.members.length > 0);
  }, [shapes, paletteFilter]);

  const onReady = (bundle: SandboxBundle) => {
    setMaterials(bundle.materials);
    setSandbox(bundle.sandbox);
    const sand = bundle.materials.findIndex((m) => m.id === "sand");
    const initial = sand >= 0 ? sand : Math.min(1, bundle.materials.length - 1);
    setSelected(initial);
    setPreviousSelected(initial);
  };

  // Tool-mode invariants
  // ---------------------
  // A click on the canvas does exactly one thing depending on
  // which mode is active: paint a material, erase, paint heat,
  // toggle a trigger, or stamp a shape. Picking any of them must
  // cleanly drop out of the others — that's what "robust" means
  // here. Each setter below is the single source of truth for
  // one mode change; they all reset the mutually-exclusive
  // siblings together.

  const onSelectMaterial = (id: number) => {
    // Picking a material is the universal "back to paint mode"
    // gesture. Drop out of every special mode and shape stamp.
    setSelected(id);
    setPreviousSelected(id);
    setEraser(false);
    setHeatMode(false);
    setActionMode(false);
    setCodeMode(false);
    setDimensionMode(false);
    setSelectedShape(null);
  };

  const onSelectShape = (shape: ShapeDef | null) => {
    setSelectedShape(shape);
    if (shape !== null) {
      setEraser(false);
      setHeatMode(false);
      setActionMode(false);
      setCodeMode(false);
      setDimensionMode(false);
    }
  };

  const toggleEraser = () => {
    if (eraser) {
      setSelected(previousSelected);
      setEraser(false);
    } else {
      setPreviousSelected(selected);
      setSelected(0);
      setEraser(true);
      setSelectedShape(null);
      setHeatMode(false);
      setActionMode(false);
      setCodeMode(false);
      setDimensionMode(false);
    }
  };

  /// Heat tool: turning it ON clears the material selection (so
  /// the sidebar visibly shows "no material highlighted, this
  /// brush draws heat") and the shape selection. Action mode +
  /// eraser also flip off so a click can never do two things at
  /// once.
  const onHeatModeWithExclusion = (next: boolean) => {
    setHeatMode(next);
    if (next) {
      setActionMode(false);
      setCodeMode(false);
      setDimensionMode(false);
      setMoveMode(false);
      setEraser(false);
      setSelectedShape(null);
      setPreviousSelected(selected);
      setSelected(0);
    } else {
      // Returning to paint mode. Restore the last material the
      // user had picked so they don't have to re-tap it after
      // every heat session.
      setSelected(previousSelected);
    }
  };

  /// Action tool: same shape as the heat-tool toggle. Turning
  /// it ON also clears the material selection — the user is no
  /// longer painting, they're flipping switches, and the
  /// sidebar should show no material highlighted. Turning it
  /// OFF restores the previously-picked material so the
  /// previous paint stroke can resume.
  const toggleActionMode = () => {
    setActionMode((prev) => {
      const next = !prev;
      if (next) {
        setHeatMode(false);
        setCodeMode(false);
        setDimensionMode(false);
        setEraser(false);
        setSelectedShape(null);
        setPreviousSelected(selected);
        setSelected(0);
      } else {
        setSelected(previousSelected);
      }
      return next;
    });
  };

  /// Code tool: same exclusion shape as the action tool. Turning it
  /// ON drops the other modes and clears the material selection (the
  /// user is inspecting programs, not painting). Turning it OFF
  /// restores the previously-picked material and closes any open
  /// Code panel.
  const toggleCodeMode = () => {
    setCodeMode((prev) => {
      const next = !prev;
      if (next) {
        setHeatMode(false);
        setActionMode(false);
        setDimensionMode(false);
        setEraser(false);
        setSelectedShape(null);
        setPreviousSelected(selected);
        setSelected(0);
      } else {
        setSelected(previousSelected);
        setCodeTarget(null);
      }
      return next;
    });
  };

  /// Dimension Switch tool: same exclusion shape as the other tools.
  /// A navigation tool — it never paints; clicking a `dimension` cell
  /// on the canvas enters its child world.
  const toggleDimensionMode = () => {
    setDimensionMode((prev) => {
      const next = !prev;
      if (next) {
        setHeatMode(false);
        setActionMode(false);
        setCodeMode(false);
        setMoveMode(false);
        setEraser(false);
        setSelectedShape(null);
        setPreviousSelected(selected);
        setSelected(0);
      } else {
        setSelected(previousSelected);
      }
      return next;
    });
  };

  /// Atomic Mode: a "snapshot the world" mode, not a paint tool. Turning
  /// it ON pauses the sim (so the drag-selected hotspot is stable) and
  /// clears the other click-driven modes; the AtomicView overlay then owns
  /// the canvas until exit. Turning it OFF unpauses and restores the
  /// previously-selected material.
  const toggleAtomicMode = () => {
    setAtomicMode((prev) => {
      const next = !prev;
      if (next) {
        setHeatMode(false);
        setActionMode(false);
        setCodeMode(false);
        setMoveMode(false);
        setDimensionMode(false);
        setEraser(false);
        setSelectedShape(null);
        setPreviousSelected(selected);
        setSelected(0);
        setPaused(true);
      } else {
        setPaused(false);
        setSelected(previousSelected);
      }
      return next;
    });
  };

  /// Move tool: turning it ON drops every other click-driven mode
  /// and clears the brush selection — the canvas is now in a
  /// click-drag-to-pan / double-click-to-pick-up state, not a paint
  /// state. Turning it OFF restores the previously-picked material.
  const toggleMoveMode = () => {
    setMoveMode((prev) => {
      const next = !prev;
      if (next) {
        setHeatMode(false);
        setActionMode(false);
        setCodeMode(false);
        setDimensionMode(false);
        setEraser(false);
        setSelectedShape(null);
        setPreviousSelected(selected);
        setSelected(0);
      } else {
        setSelected(previousSelected);
      }
      return next;
    });
  };

  const togglePaused = () => setPaused((p) => !p);
  const stepOnce = () => sandbox?.tick(0.016);
  const clearAll = () => sandbox?.clearAll(roomTemperatureK);

  // Wire the Room-temperature slider to the engine's ambient field.
  // Re-targets every empty cell's temperature to the new value (the
  // wasm path preserves non-empty cells so existing fires / lava
  // stay hot). Also bumps the heat-tool's default if the user hasn't
  // dragged it off the previous ambient — saves them a second drag
  // when they're just trying to set the scene's baseline.
  const onRoomTemperatureChange = (next: number) => {
    setBrushKelvin((bk) => (bk === roomTemperatureK ? next : bk));
    setRoomTemperatureK(next);
    sandbox?.setAmbientTemperature(next);
  };

  // Space bar toggles pause; "C" clears; WASD / arrows steer an
  // in-sandbox `cpu` (e.g. the Snake computer) via `IN 0`:
  // 1=up, 2=down, 3=left, 4=right, 0=released.
  useEffect(() => {
    // WASD + arrow keys → the CPU's keyboard port. Held key stays
    // set until keyup, then 0 ("no new key — keep going").
    const keyToDir = (e: KeyboardEvent): number | null => {
      switch (e.key.toLowerCase()) {
        case "w":
        case "arrowup":
          return 1;
        case "s":
        case "arrowdown":
          return 2;
        case "a":
        case "arrowleft":
          return 3;
        case "d":
        case "arrowright":
          return 4;
        default:
          return null;
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement) {
        const tag = e.target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
      }
      const dir = keyToDir(e);
      if (dir !== null) {
        e.preventDefault();
        sandbox?.setKeyInput?.(dir);
        return;
      }
      if (e.code === "Space") {
        e.preventDefault();
        setPaused((p) => !p);
      } else if (e.key === "c" || e.key === "C") {
        sandbox?.clearAll(roomTemperatureK);
      } else if (e.key === "e" || e.key === "E") {
        toggleEraser();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (keyToDir(e) !== null) {
        sandbox?.setKeyInput?.(0);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sandbox, eraser, selected, previousSelected, roomTemperatureK]);

  // When the user picks a material from the mobile drawer, close it
  // automatically — the canvas needs to be visible to use the brush.
  // Same logic for selecting a shape. Desktop doesn't notice because
  // the drawer state has no visual effect there.
  const closeLeftNavOnMobile = () => {
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      setLeftNavOpen(false);
    }
  };
  const onSelectMaterialMobileAware = (id: number) => {
    onSelectMaterial(id);
    closeLeftNavOnMobile();
  };
  const onSelectShapeMobileAware = (shape: ShapeDef | null) => {
    onSelectShape(shape);
    if (shape !== null) closeLeftNavOnMobile();
  };

  return (
    <div
      className={
        "app"
        + (leftNavOpen ? " left-nav-open" : "")
        + (rightNavOpen ? " right-nav-open" : "")
        + (actionMode ? " action-mode-on" : "")
        + (moveMode ? " move-mode-on" : "")
      }
    >
      <Sidebar
        materials={materials}
        selectedMaterialId={selected}
        onSelectMaterial={onSelectMaterialMobileAware}
        worldSize={worldSize}
        worldSizes={WORLD_SIZES}
        onWorldSize={setWorldSize}
        full3DMode={full3DMode}
        onFull3DMode={() =>
          setFull3DMode((v) => {
            const next = !v;
            // The 2D canvas is fully covered in Full 3D Mode, so pause
            // physics to save CPU while the user walks around (same
            // treatment the layer-stack 3D view gets).
            if (next) setPaused(true);
            return next;
          })
        }
        brushRadius={brushRadius}
        onBrushRadius={setBrushRadius}
        brushKelvin={brushKelvin}
        onBrushKelvin={setBrushKelvin}
        heatMode={heatMode}
        onHeatMode={onHeatModeWithExclusion}
        actionMode={actionMode}
        onActionMode={toggleActionMode}
        moveMode={moveMode}
        onMoveMode={toggleMoveMode}
        codeMode={codeMode}
        onCodeMode={toggleCodeMode}
        dimensionMode={dimensionMode}
        onDimensionMode={toggleDimensionMode}
        atomicMode={atomicMode}
        onAtomicMode={toggleAtomicMode}
        multiverse3D={threeDMode}
        onMultiverse3D={() =>
          setThreeDMode((v) => {
            const next = !v;
            // The layer-stack snapshot is paused — entering pauses the
            // sim so the snapshot is stable (matches the old top-right
            // toggle's behaviour).
            if (next) setPaused(true);
            return next;
          })
        }
        roomTemperatureK={roomTemperatureK}
        onRoomTemperatureK={onRoomTemperatureChange}
        filter={paletteFilter}
        onFilterChange={setPaletteFilter}
        isOpen={leftNavOpen}
        onClose={() => setLeftNavOpen(false)}
      >
        <ShapesPanel
          groups={shapeGroups}
          selectedShapeId={selectedShape?.id ?? null}
          onSelect={onSelectShapeMobileAware}
        />
      </Sidebar>
      {/* Backdrop only renders / catches clicks on mobile via CSS;
       *  on desktop it's `display: none`. Tapping anywhere outside
       *  the open drawer closes it, matching standard mobile UX. */}
      {(leftNavOpen || rightNavOpen) && (
        <div
          className="nav-backdrop"
          onClick={() => {
            setLeftNavOpen(false);
            setRightNavOpen(false);
          }}
          aria-hidden="true"
        />
      )}
      <div className="middle-col">
        <Toolbar
          paused={paused}
          onTogglePaused={togglePaused}
          eraser={eraser}
          onToggleEraser={toggleEraser}
          onClear={clearAll}
          onStep={stepOnce}
          onCycleBackground={cycleBackground}
          backgroundLabel={bg.label}
          sandbox={sandbox}
          tick={tickInfo.tick}
          backend={tickInfo.backend}
          materialCount={materials.length}
          onOpenAuth={openAuthModal}
          leftNavOpen={leftNavOpen}
          rightNavOpen={rightNavOpen}
          onToggleLeftNav={() => setLeftNavOpen((v) => !v)}
          onToggleRightNav={() => setRightNavOpen((v) => !v)}
        />
        <SimCanvas
          width={worldSize}
          height={worldSize}
          pixelScale={pixelScale}
          brushRadius={brushRadius}
          brushKelvin={brushKelvin}
          selectedMaterialId={selected}
          selectedShape={selectedShape}
          heatMode={heatMode}
          actionMode={actionMode}
          moveMode={moveMode}
          codeMode={codeMode}
          onCodeClick={(x, y) => setCodeTarget({ x, y })}
          dimensionMode={dimensionMode}
          onLayerChange={setLayerDepth}
          paused={paused}
          backgroundClass={bg.className}
          magnifierActive={magnifierActive}
          magnifierZoom={magnifierZoom}
          zoomToolActive={zoomToolActive}
          // Click-to-zoom in zoom-tool mode. Direction is +1 for a
          // plain click (one rung in) and -1 for a shift-click (one
          // rung out). SimCanvas stashes the click anchor in its own
          // ref and re-anchors the scroll position once its resize
          // effect picks up the new pixelScale, so the cell under the
          // cursor stays put across the zoom.
          computeNextZoom={(current, direction) =>
            direction > 0 ? zoomStepUp(current) : zoomStepDown(current)
          }
          onPixelScaleChange={setPixelScale}
          onReady={onReady}
          onTickInfo={setTickInfo}
        />
        {layerDepth > 0 && (
          <div className="dimension-breadcrumb">
            <span>
              Inside dimension · depth {layerDepth}
            </span>
            <button type="button" onClick={() => sandbox?.exitDimension?.()}>
              ← Back
            </button>
          </div>
        )}
        {/* The 3D Multi-Verse View toggle moved into the sidebar
            (under the Dimension tool); the view itself still renders
            here as a full-canvas overlay when active. */}
        <ThreeDView active={threeDMode} sandbox={sandbox} materials={materials} />
        <Full3DView
          active={full3DMode}
          selectedMaterial={materials[selected] ?? null}
          materials={materials}
          shapeFor={shapeFor}
          onExit={() => setFull3DMode(false)}
        />
        <AtomicView
          active={atomicMode}
          sandbox={sandbox}
          materials={materials}
          worldSize={worldSize}
          onExit={toggleAtomicMode}
        />
        <Footer
          pixelScale={pixelScale}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          magnifierActive={magnifierActive}
          onToggleMagnifier={() => setMagnifierActive((v) => !v)}
          magnifierZoom={magnifierZoom}
          onMagnifierZoom={setMagnifierZoom}
          zoomToolActive={zoomToolActive}
          onToggleZoomTool={() => setZoomToolActive((v) => !v)}
        />
      </div>
      <aside className={"formula-sidebar" + (rightNavOpen ? " is-open" : "")}>
        <button
          type="button"
          className="nav-drawer-close"
          onClick={() => setRightNavOpen(false)}
          aria-label="Close formulas and experiments panel"
          title="Close"
        >
          ×
        </button>
        <LibraryPanel
          onLoadExperiment={onLoadExperiment}
          onLoadFormula={onLoadFormula}
          onOpenAuth={openAuthModal}
          onOpenCourses={() => setCoursesOpen(true)}
          onOpenPuzzles={() => setPuzzlesOpen(true)}
        />
        <FormulaPanel
          resolver={resolver}
          sandbox={sandbox}
          source={formulaSource}
          onSourceChange={setFormulaSource}
          onOpenAuth={openAuthModal}
        />
        <ExperimentPanel
          sandbox={sandbox}
          json={experimentJson}
          onJsonChange={setExperimentJson}
          onOpenAuth={openAuthModal}
        />
        <CoursePanel onAddCourse={addCustomCourse} onOpenAuth={openAuthModal} />
      </aside>
      <CodePanel
        sandbox={sandbox}
        target={codeTarget}
        onClose={() => setCodeTarget(null)}
      />
      <AuthModal
        open={authModalOpen}
        initialMode={authModalMode}
        onClose={() => setAuthModalOpen(false)}
      />
      <CoursesModal
        open={coursesOpen}
        onClose={() => setCoursesOpen(false)}
        onPickCourse={(course) => courseRunner.start(course)}
        extraCourses={customCourses}
      />
      <PuzzlesModal
        open={puzzlesOpen}
        onClose={() => setPuzzlesOpen(false)}
        onPickPuzzle={(p) => puzzleRunner.start(p)}
        completedIds={puzzleRunner.completedIds}
      />
      {puzzleRunner.active && (
        <div
          className={
            "puzzle-banner" + (puzzleRunner.done ? " puzzle-banner-done" : "")
          }
          role="status"
          aria-live="polite"
        >
          <div className="puzzle-banner-head">
            <strong className="puzzle-banner-title">
              Puzzle · {puzzleRunner.active.name}
            </strong>
            <button
              type="button"
              className="puzzle-banner-close"
              onClick={() => puzzleRunner.cancel()}
              aria-label="Dismiss puzzle"
              title="Dismiss"
            >
              ×
            </button>
          </div>
          <p className="puzzle-banner-quest">{puzzleRunner.active.quest}</p>
          {puzzleRunner.done && (
            <p className="puzzle-banner-status">✓ Solved · saved</p>
          )}
        </div>
      )}
      <Mascot narration={courseRunner.narration} />
    </div>
  );
}
